import React from 'react';
import { toast } from 'react-toastify';
import { jsx } from '@emotion/core';
import theme from '../../theme/theme';
import ProductsTable from './components/ProductsTable';
import ProductModal from './components/ProductModal';
import {
  useProductQuery,
  useDelete_ProductMutation,
} from '../../generated/graphql';
import { IProduct } from '../../utils/types';
import UpdateHandlers from '../../utils/handlers';
import Confirm from '../../components/confirm/Confirm';
import Toastify from '../../components/toastify/Toastify';
import { useAuthState } from '../../context/auth';
import SectionTitle from '../../components/sectionTitle/SectionTitle';
import { ADMIN } from '../../routes/routes';
/**@jsx jsx*/

const Products: React.FC = () => {
  const { userId } = useAuthState();
  const [isOpen, setIsOpen] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [selectedProduct, setSelectedProduct] = React.useState<
    number | undefined
  >();
  const { data: products } = useProductQuery({
    fetchPolicy: 'cache-and-network',
  });

  const [deleteProduct] = useDelete_ProductMutation();

  const close = React.useCallback(() => {
    setIsOpen(false);
    setSelectedProduct(undefined);
    setShowConfirm(false);
  }, []);

  const edit = (product: IProduct) => {
    setIsOpen(true);
    setSelectedProduct(product.id);
  };

  const notify = (success: boolean, message: string) => {
    toast(<Toastify {...{ success, message }} />, {
      position: 'top-right',
      autoClose: 7000,
      progressStyle: {
        background: success ? theme.colors.green[200] : theme.colors.red[200],
      },
    });
  };

  const remove = async () => {
    const response = await deleteProduct({
      variables: {
        id: selectedProduct,
        doneBy: userId,
      },
      update: UpdateHandlers.deleteProduct,
    });
    setShowConfirm(false);

    if (response.data?.update_product?.affected_rows) {
      notify(true, 'Product deleted successfully.');
    } else {
      notify(false, 'failed to delete product.');
    }
  };

  const getProduct = (id?: number) => {
    if (id) {
      const product = products?.product?.find((product) => product.id === id);
      if (product) {
        return {
          id: product.id,
          name: product.name,
          fundCode: product.fund_code || '',
        };
      }
    }
    return;
  };

  const confirm = (product: IProduct) => {
    setSelectedProduct(product.id);
    setShowConfirm(true);
  };

  const getProducts = (): IProduct[] => {
    return (
      products?.product?.map((product) => ({
        id: product.id,
        name: product.name,
        fundCode: product.fund_code || '',
        createdAt: product.created_at,
        lastChanged: product.updated_at,
      })) || []
    );
  };

  return (
    <React.Fragment>
      {isOpen && (
        <ProductModal onClose={close} product={getProduct(selectedProduct)} />
      )}

      {showConfirm && (
        <Confirm
          cancel={{ label: 'cancel', function: close }}
          confirm={{ label: 'ok', function: remove }}
          onClose={close}
          title="Delete Product"
          message="Are you sure you want to delete?"
        />
      )}

      <SectionTitle
        title="Products"
        action={{
          label: 'Add',
          icon: { name: 'plus', color: theme.colors.white, position: 'after' },
          function: () => {
            setIsOpen(true);
          },
          allowed: [ADMIN],
        }}></SectionTitle>
      <ProductsTable
        products={getProducts()}
        actions={[
          { label: 'Delete', action: confirm },
          { label: 'Edit', action: edit },
        ]}
      />
    </React.Fragment>
  );
};

export default Products;
